import React, {useEffect, useState} from 'react'
import {Row, Col, message, notification} from 'antd'
import {useDispatch, useSelector} from 'react-redux'
import {Scrollbars} from 'react-custom-scrollbars'
import styles from './UploadControl.module.scss'
import {userActions} from '../Redux/_actions'
import {UploadFileItem} from '../components'
import UploadStatistic from './UploadStatistic'
import UploadDragger from './UploadDragger'
import UploadProgressBar from './UploadProgressBar'
import UploadInfo from './UploadInfo'
import UploadFooterButtons from './UploadFooterButtons'
import {selectUpload} from '../Redux/selectors/selectors'
import {useComponentLocale} from '../hooks/useComponentLocale'
import UploadDataProgressBar from "./UploadDataProgressBar";
import * as PropTypes from "prop-types";
import UploadDataInfo from "./UploadDataInfo";
import {BgColorsOutlined, CloudOutlined} from "@ant-design/icons";


const initialState = {
    fileList: [],
    filesSize: 0,
    uploading: false,
    uploadedFilesList: [],
}


function UploadDataProgressBarBar(props) {
    return null;
}

UploadDataProgressBarBar.propTypes = {
    uploadRequest: PropTypes.bool,
    uploadedFilesList: PropTypes.any
};

function UploadControl() {
    const [uploadControl, setUploadControl, notificationShown] = useState(initialState)
    const {isTaskRequest, isUploadRequest, taskList, uploadFileRequestState, uploadProgress, uploadCompleted, parseCompleted} = useSelector(selectUpload)
    const dispatch = useDispatch()

    const getLocale = useComponentLocale()
    const uploadHandsLocale = getLocale({component: 'navigation', section: 'upload_hands'})

    const uploadRequest = isUploadRequest && uploadControl.uploading

    const openNotification = () => {
        notification.info({
            message: 'Hand history uploaded',
            description: 'Your HH files were uploaded to server. You can safely continue to use the application and check the upload task list later',
            placement: 'bottomLeft',
            icon: <CloudOutlined />,
            duration: 15,
        })
    }

    let fileSizeStateless = 0

    useEffect(() => {
        if (uploadCompleted){
            openNotification()
        }

    }, [uploadCompleted])

    useEffect(() => {
        dispatch(userActions.getTaskList())

        return () => {
            dispatch(userActions.newUpload())
        }
    }, [])

    useEffect(() => {
        const {fileList} = uploadControl

        if (!uploadFileRequestState || !uploadControl.uploading) {
            return
        }

        const current = uploadFileRequestState.progressCurrent

        if (!current) {
            return
        }

        if (current.status === 'added' || current.status === 'error') {
            const uploaded = fileList.find((file) => file.name === current.fileName)
            setUploadControl((prevState) => ({
                ...prevState,
                uploadedFilesList: [...prevState.uploadedFilesList, uploaded],
            }))
        }

        const file = fileList.find((file) => {
            if (file.name === current.fileName) {
                file.status = current.status
            }
        })

        if (file) {
            setUploadControl((prevState) => ({
                ...prevState,
                fileList: [...fileList],
            }))
        }
    }, [uploadFileRequestState, uploadControl.uploading, uploadControl.fileList.length, uploadProgress, uploadCompleted, parseCompleted])

    const handleUpload = () => {
        const formData = new FormData()
        uploadControl.fileList.forEach((file) => {
            formData.append('files', file)
        })
        setUploadControl((prevState) => ({...prevState, uploading: true}))
        dispatch(userActions.uploadHandHistory(formData))

    }

    const onDragBeforeUpload = (file) => {
        const fileSize = fileSizeStateless
        const isTxt = file.type === 'text/plain' || file.type === 'text/xml'

        if (!isTxt) {
            message.error('Only text files are supported.')
            return
        }

        const size = fileSize + file.size
        const isLt100M = size < 100 * 1024 * 1024

        if (!isLt100M) {
            message.error('The upload size > 100MB. Please use another upload.')
            return
        }

        fileSizeStateless = size

        setUploadControl((prev) => ({
            ...prev,
            fileList: [...prev.fileList, file],
            filesSize: fileSizeStateless,
        }))

        return false
    }

    const onDragFileRemove = (file) => {
        setUploadControl((state) => {
            const fileIndex = state.fileList.indexOf(file)
            const newFileList = state.fileList.slice()

            newFileList.splice(fileIndex, 1)
            fileSizeStateless = uploadControl.filesSize - file.size

            return {
                ...state,
                fileList: newFileList,
                filesSize: fileSizeStateless,
            }
        })
    }

    const onButtonStatusClicked = (job_id, status) => {
        dispatch(userActions.updateStatus(job_id, status))
    }

    const handleClearAll = () => {
        fileSizeStateless = 0
        setUploadControl({...initialState, fileSize: fileSizeStateless})

        dispatch(userActions.newUpload())
    }

    const refreshUploads = () => {
        dispatch(userActions.getTaskList())
    }

    const dragProps = {
        name: 'file',
        multiple: true,
        listType: 'file',
        showUploadList: false,
        beforeUpload: onDragBeforeUpload,
        fileList: uploadControl.fileList,
        accept: '.txt, .xml',
    }

    return (
        <Scrollbars
            renderView={({style, ...props}) => <div {...props} style={{...style, paddingLeft: 30, paddingRight: 30}}/>}
            renderThumbVertical={({style, ...props}) => (
                <div {...props} style={{...style, backgroundColor: '#5a6268', borderRadius: '3px'}}/>
            )}
        >
            <Row gutter={{xs: 0, sm: 0, md: 24, lg: 24, xl: 24}} className={styles.generalContainer}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} className={styles.leftArea}>
                    <Row className={styles.leftContainer}>
                        <div>
                            <h1>{uploadHandsLocale}</h1>
                        </div>

                        <UploadDragger
                            uploadRequest={uploadRequest}
                            dragProps={dragProps}
                            uploadedFilesList={uploadControl.uploadedFilesList}
                            fileList={uploadControl.fileList}
                        />

                        <UploadDataProgressBar
                            uploadProgress={uploadProgress}
                        />

                        <UploadProgressBar
                            uploadRequest={uploadRequest}
                            uploadProgress={uploadProgress}
                            uploadedFilesList={uploadControl.uploadedFilesList}
                            uploadFileRequestState={uploadFileRequestState}
                        />

                        <UploadDataInfo
                            uploadProgress={uploadProgress}
                        />

                        <UploadInfo
                            uploadRequest={uploadRequest}
                            uploadedFilesList={uploadControl.uploadedFilesList}
                            uploadFileRequestState={uploadFileRequestState}
                        />

                        <UploadFileItem
                            isUploading = {!!uploadProgress}
                            isUploadFinished = {uploadCompleted}
                            isParsing = {isUploadRequest}
                            isFullFinished = {parseCompleted}
                            fileList={uploadControl.fileList}
                            onRemove={onDragFileRemove}
                        />

                        <UploadFooterButtons
                            uploadRequest={uploadRequest}
                            uploadedFilesList={uploadControl.uploadedFilesList}
                            fileList={uploadControl.fileList}
                            handleClearAll={handleClearAll}
                            filesSize={uploadControl.filesSize}
                            handleUpload={handleUpload}
                        />
                    </Row>
                </Col>

                <UploadStatistic
                    taskList={taskList}
                    isTaskRequest={isTaskRequest}
                    refreshUploads={refreshUploads}
                    onButtonStatusClicked={onButtonStatusClicked}
                />
            </Row>
        </Scrollbars>
    )
}

export default UploadControl
